import React, { useEffect, useRef, useState } from "react";

import ReactCountryFlag from "react-country-flag";
import Select from "react-select";
import { getCountries } from "react-phone-number-input";

import userController from "../../controllers/userController";
import { userService } from "../../services/userService";
import {
  isApiError,
  isSuccessResponse,
} from "../../registrationFlow/registrationHelpers";
import Utils from "../../utils/Utils";
import ChooseNicknameIcon from "../../icons/Address/ChooseNicknameIcon";
import CityIcon from "../../icons/Address/CityIcon";
import CountryOfResidenceIcon from "../../icons/Address/CountryOfResidenceIcon";
import MapLocationIcon from "../../icons/Address/MapLocationIcon";
import StreetAddressIcon from "../../icons/Address/StreetAddressIcon";
import ZipCodeIcon from "../../icons/Address/ZipCodeIcon";
import AccountActionButton from "./AccountActionButton";
import DeleteAddressModal from "./DeleteAddressModal";
import { getPortalHomePath } from "../access";
import { useNavigate } from "react-router-dom";
import BusinessLocationIcon from "../../icons/Address/BusinessLocationIcon";
import {
  translateText,
  translateLabel,
  useTranslationSnapshot,
} from "./../../translations/runtime";

// =========================================================
// COUNTRY
// =========================================================

const countryNameFormatter =
  typeof Intl !== "undefined" && Intl.DisplayNames
    ? new Intl.DisplayNames(["en"], {
        type: "region",
      })
    : null;

const countryOptions = getCountries()
  .map((countryCode) => {
    const countryName = countryNameFormatter?.of(countryCode) || countryCode;

    return {
      value: countryName,
      label: countryName,
      countryCode,
    };
  })
  .sort((a, b) => a.label.localeCompare(b.label));

function CountryOption({ countryCode, label }) {
  useTranslationSnapshot();
  return (
    <span
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
      }}
    >
      <ReactCountryFlag
        countryCode={countryCode}
        svg
        style={{
          width: "20px",
          height: "15px",
        }}
      />

      <span>{translateText(label)}</span>
    </span>
  );
}

// =========================================================
// INITIAL FORM
// =========================================================

const initialAddressForm = {
  nickname: "",
  customNickname: "",
  country: "",
  city: "",
  zipCode: "",
  streetAddress: "",
  complimentaryAddress: "",
  isDefault: false,

  pinnedAddress: "",
  streetNumber: "",

  addressID: "",
  userAddressID: "",

  latitude: null,
  longitude: null,
};

// =========================================================
// RESPONSE
// =========================================================

const getResponseData = (response) => {
  return (
    response?.data?.data ??
    response?.data?.result ??
    response?.data ??
    response ??
    null
  );
};

// =========================================================
// SESSION
// =========================================================

const getJwtFromStoredUser = (storedUser) => {
  if (typeof storedUser === "string") {
    return storedUser;
  }

  return (
    storedUser?.jwt ||
    storedUser?.token ||
    storedUser?.accessToken ||
    storedUser?.access_token ||
    ""
  );
};

const getUserIdFromPayload = (payload = {}) =>
  String(
    payload?.id ||
      payload?.userId ||
      payload?.userID ||
      payload?.UserId ||
      payload?.UserID ||
      payload?.sub ||
      payload?.user?.id ||
      payload?.user?.userId ||
      payload?.user?.userID ||
      "",
  ).trim();

const getCompanyIdFromPayload = (payload = {}) =>
  String(
    payload?.companyId ||
      payload?.companyID ||
      payload?.company?.id ||
      Object.keys(payload?.userDetails || {})[0] ||
      "",
  ).trim();

const resolveAuthenticatedSession = () => {
  const storedUser = userService.getJwt();

  const jwt = String(getJwtFromStoredUser(storedUser) || "").trim();

  const payload =
    (jwt ? Utils.parseJwt(jwt) : null) || userService.getUserPayload() || {};
  const userData = userService.getUserData();
  const userID = getUserIdFromPayload(payload);
  const companyID = userData.UserID === userID ? userData.Compid : "";

  return {
    jwt,
    userID,
    companyID: companyID || getCompanyIdFromPayload(payload),
  };
};

// =========================================================
// BACKEND -> FORM
// =========================================================

const mapBackendAddressToForm = (address = {}) => {
  const rawLabel = String(address?.label || "").toUpperCase();

  let nickname = "";
  let customNickname = "";

  if (rawLabel === "HOME") {
    nickname = "HOME";
  } else if (rawLabel === "WORK") {
    nickname = "WORK";
  } else if (rawLabel === "FRIENDS" || rawLabel === "FRIEND") {
    nickname = "FRIENDS";
  } else {
    nickname = "CUSTOM";
    customNickname = address?.customeLable || "";
  }

  return {
    nickname,
    customNickname,

    country: address?.country || "",

    city: address?.locality || "",

    zipCode: address?.postalCode || "",

    streetAddress: address?.route || "",

    complimentaryAddress: address?.postalCodeSuffix || "",

    isDefault: Boolean(address?.defaultAddress ?? address?.default),

    pinnedAddress: address?.pinnedAddress || "",

    streetNumber:
      address?.streetNumber != null ? String(address.streetNumber) : "",

    addressID: address?.addressID || "",

    userAddressID: address?.userAddressID || "",

    latitude: null,
    longitude: null,
  };
};

// =========================================================
// LABEL
// =========================================================

const getBackendLabel = (nickname) => {
  switch (
    String(nickname || "")
      .trim()
      .toLowerCase()
  ) {
    case "home":
      return "HOME";

    case "work":
      return "WORK";

    case "Custom":
      return "CUSTOM";

    default:
      return "CUSTOM";
  }
};

const getCustomLabelValue = ({ nickname, customNickname }) => {
  const selectedNickname = String(nickname || "").trim();

  if (selectedNickname.toUpperCase() === "CUSTOM") {
    return String(customNickname || "").trim();
  }

  return selectedNickname;
};

// =========================================================
// LEAFLET
// =========================================================

const loadLeaflet = () => {
  return new Promise((resolve, reject) => {
    if (window.L) {
      resolve(window.L);
      return;
    }

    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");

      link.id = "leaflet-css";
      link.rel = "stylesheet";

      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";

      document.head.appendChild(link);
    }

    const existingScript = document.getElementById("leaflet-script");

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.L));

      existingScript.addEventListener("error", reject);

      return;
    }

    const script = document.createElement("script");

    script.id = "leaflet-script";

    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";

    script.async = true;

    script.onload = () => resolve(window.L);

    script.onerror = reject;

    document.body.appendChild(script);
  });
};

// =========================================================
// COMPONENT
// =========================================================

function AddressPanel({ backSignal = 0, onGoHome, mode, onSaved }) {
  useTranslationSnapshot();
  const [addressStep, setAddressStep] = useState("empty");
  const navigate = useNavigate();

  const [addressForm, setAddressForm] = useState(initialAddressForm);

  const [savedAddresses, setSavedAddresses] = useState([]);

  const [editingAddressIndex, setEditingAddressIndex] = useState(null);

  const [deletingAddressIndex, setDeletingAddressIndex] = useState(null);

  const [isAddressMapOpen, setIsAddressMapOpen] = useState(false);

  const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);

  const [isSavingAddress, setIsSavingAddress] = useState(false);

  const [isDeletingAddress, setIsDeletingAddress] = useState(false);

  const [addressError, setAddressError] = useState("");

  const [mapError, setMapError] = useState("");

  const [isResolvingLocation, setIsResolvingLocation] = useState(false);

  const [selectedMapLocation, setSelectedMapLocation] = useState(null);

  const hasMounted = useRef(false);

  const mapContainerRef = useRef(null);

  const mapInstanceRef = useRef(null);

  const markerRef = useRef(null);

  // =======================================================
  // FIELD
  // =======================================================

  const updateAddressField = (field, value) => {
    setAddressForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  // =======================================================
  // LOAD ADDRESSES
  // =======================================================

  const loadAddresses = async () => {
    try {
      setIsLoadingAddresses(true);

      setAddressError("");

      const { jwt, userID } = resolveAuthenticatedSession();

      if (!jwt || !userID) {
        setSavedAddresses([]);

        setAddressStep("empty");

        setAddressError("Unable to identify current user.");

        return;
      }

      const response = await userController.getUserAddress({
        userID,
        jwt,
      });
      if (isApiError(response) || !isSuccessResponse(response)) throw response;

      const data = getResponseData(response);

      const addresses = Array.isArray(data)
        ? data
        : Array.isArray(data?.addresses)
          ? data.addresses
          : [];

      setSavedAddresses(addresses);

      setAddressStep(
        addresses.length ? "list" : mode === "registration" ? "form" : "empty",
      );
    } catch (error) {
      console.error("[ADDRESS] GET FAILED:", error?.response?.data || error);

      setSavedAddresses([]);

      setAddressStep("empty");

      setAddressError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to load addresses.",
      );
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  // =======================================================
  // INITIAL LOAD
  // =======================================================

  useEffect(() => {
    loadAddresses();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =======================================================
  // BACK
  // =======================================================

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    if (isAddressMapOpen) {
      setIsAddressMapOpen(false);

      return;
    }

    if (deletingAddressIndex !== null) {
      setDeletingAddressIndex(null);

      return;
    }

    if (addressStep !== "empty" && addressStep !== "list") {
      setAddressForm(initialAddressForm);

      setEditingAddressIndex(null);

      setAddressStep(savedAddresses.length ? "list" : "empty");

      return;
    }

    onGoHome?.();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backSignal]);

  // =======================================================
  // REVERSE GEOCODE
  // =======================================================

  const reverseGeocode = async (latitude, longitude) => {
    try {
      setIsResolvingLocation(true);

      setMapError("");

      const url =
        "https://nominatim.openstreetmap.org/reverse" +
        "?format=jsonv2" +
        `&lat=${encodeURIComponent(latitude)}` +
        `&lon=${encodeURIComponent(longitude)}` +
        "&addressdetails=1";

      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Unable to get address from selected location.");
      }

      const data = await response.json();

      const details = data?.address || {};

      const country = details?.country || "";

      const city =
        details?.city ||
        details?.town ||
        details?.village ||
        details?.municipality ||
        details?.county ||
        details?.state_district ||
        details?.state ||
        "";

      const postalCode = details?.postcode || "";

      const route =
        details?.road ||
        details?.pedestrian ||
        details?.footway ||
        details?.residential ||
        details?.neighbourhood ||
        details?.suburb ||
        "";

      const streetNumber = details?.house_number || "";

      const pinnedAddress =
        data?.display_name ||
        [streetNumber, route, city, country].filter(Boolean).join(", ");

      const location = {
        latitude,
        longitude,
        country,
        city,
        postalCode,
        route,
        streetNumber,
        pinnedAddress,
      };

      setSelectedMapLocation(location);

      return location;
    } catch (error) {
      console.error("[MAP] REVERSE GEOCODE FAILED:", error);

      setSelectedMapLocation(null);

      setMapError(error?.message || "Unable to get selected address.");

      return null;
    } finally {
      setIsResolvingLocation(false);
    }
  };

  // =======================================================
  // MAP
  // =======================================================

  useEffect(() => {
    if (!isAddressMapOpen) {
      return undefined;
    }

    let cancelled = false;

    const initializeMap = async () => {
      try {
        setMapError("");

        const L = await loadLeaflet();

        if (cancelled || !mapContainerRef.current) {
          return;
        }

        if (mapInstanceRef.current) {
          mapInstanceRef.current.remove();

          mapInstanceRef.current = null;

          markerRef.current = null;
        }

        let initialLat = 33.8938;

        let initialLng = 35.5018;

        let initialZoom = 9;

        if (addressForm.latitude != null && addressForm.longitude != null) {
          initialLat = addressForm.latitude;

          initialLng = addressForm.longitude;

          initialZoom = 16;
        }

        const map = L.map(mapContainerRef.current).setView(
          [initialLat, initialLng],
          initialZoom,
        );

        mapInstanceRef.current = map;

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution: "&copy; OpenStreetMap contributors",
        }).addTo(map);

        if (addressForm.latitude != null && addressForm.longitude != null) {
          markerRef.current = L.marker([
            addressForm.latitude,
            addressForm.longitude,
          ]).addTo(map);
        }

        map.on("click", async (event) => {
          const { lat, lng } = event.latlng;

          if (markerRef.current) {
            markerRef.current.setLatLng([lat, lng]);
          } else {
            markerRef.current = L.marker([lat, lng]).addTo(map);
          }

          await reverseGeocode(lat, lng);
        });

        setTimeout(() => {
          map.invalidateSize();
        }, 150);
      } catch (error) {
        console.error("[MAP] LOAD FAILED:", error);

        setMapError("Unable to load map.");
      }
    };

    initializeMap();

    return () => {
      cancelled = true;

      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();

        mapInstanceRef.current = null;

        markerRef.current = null;
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddressMapOpen]);

  // =======================================================
  // USE MAP LOCATION
  // =======================================================

  const useSelectedMapLocation = () => {
    if (!selectedMapLocation) {
      setMapError("Please select a location on the map first.");

      return;
    }

    setAddressForm((current) => ({
      ...current,

      country: selectedMapLocation.country || current.country,

      city: selectedMapLocation.city || current.city,

      zipCode: selectedMapLocation.postalCode || current.zipCode,

      streetAddress: selectedMapLocation.route || current.streetAddress,

      pinnedAddress: selectedMapLocation.pinnedAddress || current.pinnedAddress,

      streetNumber: selectedMapLocation.streetNumber || current.streetNumber,

      latitude: selectedMapLocation.latitude,

      longitude: selectedMapLocation.longitude,
    }));

    setIsAddressMapOpen(false);
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setMapError("Current location is not available in this browser.");

      return;
    }

    setMapError("");
    setIsResolvingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;

          if (mapInstanceRef.current) {
            mapInstanceRef.current.setView([latitude, longitude], 16);

            if (markerRef.current) {
              markerRef.current.setLatLng([latitude, longitude]);
            } else {
              const L = await loadLeaflet();

              markerRef.current = L.marker([latitude, longitude]).addTo(
                mapInstanceRef.current,
              );
            }
          }

          await reverseGeocode(latitude, longitude);
        } catch (error) {
          console.error("[MAP] CURRENT LOCATION SELECT FAILED:", error);

          setMapError("Unable to use your current location.");

          setIsResolvingLocation(false);
        }
      },
      (error) => {
        console.error("[MAP] CURRENT LOCATION FAILED:", error);

        setIsResolvingLocation(false);

        setMapError(
          error?.code === error.PERMISSION_DENIED
            ? "Location permission was denied."
            : "Unable to get your current location.",
        );
      },
      {
        enableHighAccuracy: true,
        maximumAge: 60000,
        timeout: 10000,
      },
    );
  };

  // =======================================================
  // ADD
  // =======================================================

  const openAddAddress = () => {
    setAddressError("");

    setAddressForm(initialAddressForm);

    setSelectedMapLocation(null);

    setEditingAddressIndex(null);

    setAddressStep("form");
  };

  // =======================================================
  // EDIT
  // =======================================================

  const editAddress = (address, index) => {
    setAddressError("");

    setAddressForm(mapBackendAddressToForm(address));

    setSelectedMapLocation(null);

    setEditingAddressIndex(index);

    setAddressStep("form");
  };

  // =======================================================
  // VALIDATION
  // =======================================================

  const validateAddress = () => {
    if (!String(addressForm.nickname || "").trim()) {
      return "Please choose a nickname.";
    }

    if (
      String(addressForm.nickname || "")
        .trim()
        .toUpperCase() === "CUSTOM" &&
      !String(addressForm.customNickname || "").trim()
    ) {
      return "Please enter a custom nickname.";
    }

    if (!String(addressForm.country || "").trim()) {
      return "Please select a country.";
    }

    if (!String(addressForm.city || "").trim()) {
      return "Please enter the city.";
    }

    if (!String(addressForm.zipCode || "").trim()) {
      return "Please enter the zip code.";
    }

    if (!String(addressForm.streetAddress || "").trim()) {
      return "Please enter the street address or select it from the map.";
    }

    return "";
  };

  // =======================================================
  // SAVE
  // =======================================================

  const saveAddress = async () => {
    const validationError = validateAddress();

    if (validationError) {
      setAddressError(validationError);

      return;
    }

    try {
      setIsSavingAddress(true);

      setAddressError("");

      const { jwt, companyID } = resolveAuthenticatedSession();

      if (!jwt) {
        setAddressError("Unable to identify current user.");

        return;
      }

      if (mode === "registration" && !companyID) {
        setAddressError(
          "Unable to identify the registered company. Please sign in again.",
        );
        return;
      }

      const isEditing = editingAddressIndex !== null;

      const existingAddress = isEditing
        ? savedAddresses[editingAddressIndex]
        : null;

      const userAddressID =
        addressForm.userAddressID || existingAddress?.userAddressID || "";

      const addressID =
        addressForm.addressID || existingAddress?.addressID || "";

      const nickname = String(addressForm.nickname || "").trim();
      const customeLable = getCustomLabelValue(addressForm);

      const rawStreetNumber = String(addressForm.streetNumber || "").trim();

      const streetNumber =
        rawStreetNumber && !Number.isNaN(Number(rawStreetNumber))
          ? Number(rawStreetNumber)
          : 0;

      const addressPayload = {
        country: String(addressForm.country || "").trim(),

        customeLable,

        defaultAddress: Boolean(addressForm.isDefault),

        label: getBackendLabel(nickname),

        locality: String(addressForm.city || "").trim(),

        pinnedAddress: String(
          addressForm.pinnedAddress || addressForm.streetAddress || "",
        ).trim(),

        postalCode: String(addressForm.zipCode || "").trim(),

        postalCodeSuffix: String(addressForm.complimentaryAddress || "").trim(),

        route: String(addressForm.streetAddress || "").trim(),

        streetNumber,
      };

      if (addressID) {
        addressPayload.addressID = addressID;
      }

      if (userAddressID) {
        addressPayload.userAddressID = userAddressID;
      }

      const userAddress = {
        address: addressPayload,

        defaultAddress: Boolean(addressForm.isDefault),
      };

      if (companyID) {
        userAddress.companyID = companyID;
      }

      if (userAddressID) {
        userAddress.userAddressID = userAddressID;
      }

      const response = await userController.setUserAddress({
        userAddress,
        jwt,
      });
      if (isApiError(response) || !isSuccessResponse(response)) throw response;

      if (onSaved) {
        onSaved();
        return;
      }

      setAddressForm(initialAddressForm);

      setSelectedMapLocation(null);

      setEditingAddressIndex(null);

      await loadAddresses();
      if (mode === "Sign-in") {
        navigate(getPortalHomePath(userService.getUserPayload()));
      }
    } catch (error) {
      console.error("[ADDRESS] SAVE FAILED:", error?.response?.data || error);

      setAddressError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to save address.",
      );
    } finally {
      setIsSavingAddress(false);
    }
  };

  // =======================================================
  // DELETE
  // =======================================================

  const deleteAddress = async () => {
    if (deletingAddressIndex === null) {
      return;
    }

    const address = savedAddresses[deletingAddressIndex];

    const userAddressID = String(address?.userAddressID || "").trim();

    if (!userAddressID) {
      setAddressError("Address ID is missing.");

      setDeletingAddressIndex(null);

      return;
    }

    try {
      setIsDeletingAddress(true);

      setAddressError("");

      const { jwt } = resolveAuthenticatedSession();

      if (!jwt) {
        setAddressError("Unable to identify current user.");

        return;
      }

      const response = await userController.deleteUserAddress({
        userAddressID,
        jwt,
      });

      setDeletingAddressIndex(null);

      await loadAddresses();
    } catch (error) {
      console.error("[ADDRESS] DELETE FAILED:", error?.response?.data || error);

      setAddressError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to delete address.",
      );
    } finally {
      setIsDeletingAddress(false);
    }
  };

  // =======================================================
  // DEFAULT
  // =======================================================

  const setAsDefault = async (address) => {
    try {
      setAddressError("");

      const form = mapBackendAddressToForm(address);

      const { jwt, companyID } = resolveAuthenticatedSession();

      if (!jwt) {
        setAddressError("Unable to identify current user.");

        return;
      }

      const payload = {
        address: {
          addressID: form.addressID,

          country: form.country,

          customeLable: getCustomLabelValue(form),

          defaultAddress: true,

          label: getBackendLabel(form.nickname),

          locality: form.city,

          pinnedAddress: form.pinnedAddress || form.streetAddress,

          postalCode: form.zipCode,

          postalCodeSuffix: form.complimentaryAddress || "",

          route: form.streetAddress,

          streetNumber: Number(form.streetNumber || 0),

          userAddressID: form.userAddressID,
        },

        defaultAddress: true,

        userAddressID: form.userAddressID,
      };

      if (companyID) {
        payload.companyID = companyID;
      }

      await userController.setUserAddress({
        userAddress: payload,
        jwt,
      });

      await loadAddresses();
    } catch (error) {
      console.error(
        "[ADDRESS] DEFAULT FAILED:",
        error?.response?.data || error,
      );

      setAddressError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to set default address.",
      );
    }
  };

  // =======================================================
  // CLOSE FORM
  // =======================================================

  const closeForm = () => {
    setAddressForm(initialAddressForm);

    setSelectedMapLocation(null);

    setEditingAddressIndex(null);

    setAddressError("");

    setAddressStep(savedAddresses.length ? "list" : "empty");
  };

  // =======================================================
  // OPEN MAP
  // =======================================================

  const openMap = () => {
    setMapError("");

    if (addressForm.latitude != null && addressForm.longitude != null) {
      setSelectedMapLocation({
        latitude: addressForm.latitude,

        longitude: addressForm.longitude,

        country: addressForm.country,

        city: addressForm.city,

        postalCode: addressForm.zipCode,

        route: addressForm.streetAddress,

        streetNumber: addressForm.streetNumber,

        pinnedAddress: addressForm.pinnedAddress,
      });
    } else {
      setSelectedMapLocation(null);
    }

    setIsAddressMapOpen(true);
  };

  const openDeleteAddressModal = (index) => {
    setDeletingAddressIndex(index);
  };

  // =======================================================
  // COUNTRY SELECT VALUE
  // =======================================================

  const selectedCountryOption =
    countryOptions.find((option) => option.value === addressForm.country) ||
    null;

  const selectedDeleteAddress =
    deletingAddressIndex !== null ? savedAddresses[deletingAddressIndex] : null;

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <div className="vendor-account-address-flow">
      {mode === "Sign-in" ? (
        <div className="business-address-header">
          <div className="business-address-icon">
            {/* <i className="bi bi-geo-alt"> */}
            <BusinessLocationIcon />
            {/* </i> */}
          </div>

          <h2 className="business-address-title">
            {translateLabel(
              "txt_web_vendor_add_business_address",
              "Add Your Business Address",
            )}
          </h2>

          <p className="business-address-subtitle">
            {translateLabel(
              "txt_web_vendor_business_address_setup_description",
              "Provide your business address to complete your account setup and continue.",
            )}
          </p>
        </div>
      ) : null}

      <div className="vendor-account-email-path">
        <span>{translateLabel("txt_address", "Address")}</span>
      </div>

      <p className="vendor-account-email-copy">
        {translateLabel("txt_manage_addresses", "Manage your addresses")}
      </p>

      {addressError ? (
        <p className="vendor-account-password-error" role="alert">
          {translateText(addressError)}
        </p>
      ) : null}

      {/* =============================================== */}
      {/* LOADING */}
      {/* =============================================== */}

      {isLoadingAddresses ? (
        <div className="vendor-account-address-empty">
          <p>
            {translateLabel(
              "txt_web_vendor_loading_addresses",
              "Loading addresses...",
            )}
          </p>
        </div>
      ) : null}

      {/* =============================================== */}
      {/* EMPTY */}
      {/* =============================================== */}

      {!isLoadingAddresses && addressStep === "empty" ? (
        <div className="vendor-account-address-empty">
          <p>
            {translateLabel(
              "txt_end_user_no_address_added_yet",
              "No Address Added Yet?",
            )}
          </p>

          <AccountActionButton
            className="vendor-account-info-save vendor-account-address-add"
            onClick={openAddAddress}
            width="285px"
          >
            {translateLabel("btn_add_new_address", "+ Add New Address")}
          </AccountActionButton>
        </div>
      ) : null}

      {/* =============================================== */}
      {/* FORM */}
      {/* =============================================== */}

      {!isLoadingAddresses && addressStep === "form" ? (
        <form
          className="vendor-account-address-form"
          onSubmit={(event) => {
            event.preventDefault();

            saveAddress();
          }}
        >
          {/* MAP */}

          <div className="vendor-account-address-map-row">
            <span>
              {translateLabel(
                "txt_web_vendor_choose_from_map",
                "Choose from map",
              )}
            </span>

            <button
              onClick={openMap}
              type="button"
              aria-label={translateLabel(
                "txt_web_vendor_choose_from_map",
                "Choose from map",
              )}
            >
              <MapLocationIcon />
            </button>
          </div>

          {addressForm.pinnedAddress ? (
            <div className="vendor-account-address-map-selected">
              <strong>
                {translateLabel(
                  "txt_web_vendor_selected_location",
                  "Selected location",
                )}
              </strong>

              <p>{addressForm.pinnedAddress}</p>
            </div>
          ) : null}

          {/* NICKNAME */}

          <label className="vendor-account-address-field vendor-account-address-wide">
            <span>
              <ChooseNicknameIcon />{" "}
              {translateLabel(
                "input_address_nickname",
                "Choose a Nickname",
              )}{" "}
            </span>

            <select
              value={addressForm.nickname}
              onChange={(event) => {
                const nextNickname = event.target.value;

                setAddressForm((current) => ({
                  ...current,
                  nickname: nextNickname,
                  customNickname:
                    nextNickname === "CUSTOM" ? current.customNickname : "",
                }));
              }}
            >
              <option value="">
                {translateLabel(
                  "input_address_nickname_placeholder",
                  "Select a nickname",
                )}
              </option>

              <option value="HOME">
                {translateLabel("input_address_nickname_home", "Home")}
              </option>

              <option value="WORK">
                {translateLabel("txt_end_user_work", "Work")}
              </option>

              <option value="CUSTOM">
                {translateLabel("txt_end_user_custom", "Custom")}
              </option>
            </select>

            {addressForm.nickname === "CUSTOM" ? (
              <input
                className="custom-input-nickname"
                placeholder={translateLabel(
                  "input_web_vendor_address_nickname_placeholder",
                  "Enter nickname",
                )}
                value={addressForm.customNickname}
                onChange={(event) =>
                  updateAddressField("customNickname", event.target.value)
                }
              />
            ) : null}
          </label>

          {/* COUNTRY */}

          <div className="vendor-account-address-field vendor-account-address-wide">
            <span>
              <CountryOfResidenceIcon />{" "}
              {translateLabel(
                "input_country_of_residence",
                "Country of Residence",
              )}{" "}
              <i>*</i>
            </span>

            <Select
              className="vendor-account-address-country-select"
              classNamePrefix="vendor-account-address-country"
              options={countryOptions}
              value={selectedCountryOption}
              placeholder={translateLabel(
                "input_country_of_residence_placeholder",
                "Select country of residence",
              )}
              isSearchable
              menuPlacement="auto"
              menuPosition="fixed"
              formatOptionLabel={(option) => <CountryOption {...option} />}
              onChange={(option) => {
                updateAddressField("country", option?.value || "");
              }}
            />
          </div>

          {/* CITY / ZIP */}

          <div className="vendor-account-address-grid">
            <label className="vendor-account-address-field">
              <span>
                <CityIcon /> {translateLabel("input_city", "City")}
                <i>*</i>
              </span>

              <input
                value={addressForm.city}
                onChange={(event) =>
                  updateAddressField("city", event.target.value)
                }
              />
            </label>

            <label className="vendor-account-address-field">
              <span>
                <ZipCodeIcon /> {translateLabel("input_zip_code", "Zip Code")}
                <i>*</i>
              </span>

              <input
                value={addressForm.zipCode}
                onChange={(event) =>
                  updateAddressField("zipCode", event.target.value)
                }
              />
            </label>
          </div>

          {/* STREET */}

          <label className="vendor-account-address-field vendor-account-address-wide">
            <span>
              <StreetAddressIcon />{" "}
              {translateLabel("input_street_address", "Street Address")}{" "}
              <i>*</i>
            </span>

            <input
              value={addressForm.streetAddress}
              onChange={(event) =>
                updateAddressField("streetAddress", event.target.value)
              }
            />
          </label>

          {/* COMPLIMENTARY */}

          <label className="vendor-account-address-field vendor-account-address-wide">
            <span>
              <StreetAddressIcon />{" "}
              {translateLabel(
                "input_complimentary_address",
                "Complimentary Address",
              )}{" "}
            </span>

            <input
              value={addressForm.complimentaryAddress}
              onChange={(event) =>
                updateAddressField("complimentaryAddress", event.target.value)
              }
            />
          </label>

          {/* DEFAULT */}

          <label className="vendor-account-address-default">
            <span>
              {translateLabel(
                "input_default_address",
                "Set as default address",
              )}
            </span>

            <input
              checked={addressForm.isDefault}
              onChange={(event) =>
                updateAddressField("isDefault", event.target.checked)
              }
              type="checkbox"
            />
          </label>

          {/* ACTIONS */}

          <div className="vendor-account-email-actions">
            <AccountActionButton
              onClick={saveAddress}
              disabled={isSavingAddress}
            >
              {isSavingAddress
                ? translateLabel("txt_end_user_saving", "Saving...")
                : editingAddressIndex === null
                  ? translateLabel("btn_save", "Save")
                  : translateLabel("btn_update", "Update")}
            </AccountActionButton>

            <AccountActionButton
              color="dark"
              onClick={closeForm}
              disabled={isSavingAddress}
            >
              {" "}
              {translateLabel("btn_cancel", "Cancel")}{" "}
            </AccountActionButton>
          </div>
        </form>
      ) : null}

      {/* =============================================== */}
      {/* LIST */}
      {/* =============================================== */}

      {!isLoadingAddresses && addressStep === "list" ? (
        <div className="vendor-account-address-list">
          {savedAddresses.map((address, index) => {
            const isDefault = Boolean(
              address?.defaultAddress ?? address?.default,
            );

            return (
              <div
                className="vendor-account-address-card"
                key={address?.userAddressID || address?.addressID || index}
              >
                <div>
                  <strong>
                    {address?.customeLable ||
                      address?.label ||
                      translateLabel("txt_address", "Address")}

                    {isDefault ? (
                      <span>{translateLabel("txt_default", "Default")}</span>
                    ) : null}
                  </strong>

                  <p>{address?.route || address?.pinnedAddress || "-"}</p>

                  <p>
                    {[address?.locality, address?.country, address?.postalCode]
                      .filter(Boolean)
                      .join(", ")}
                  </p>

                  {address?.pinnedAddress ? (
                    <p>{address.pinnedAddress}</p>
                  ) : null}

                  {!isDefault ? (
                    <button type="button" onClick={() => setAsDefault(address)}>
                      {" "}
                      {translateLabel(
                        "txt_end_user_set_as_default",
                        "Set as default",
                      )}{" "}
                    </button>
                  ) : null}
                </div>

                <div>
                  <button
                    onClick={() => editAddress(address, index)}
                    type="button"
                  >
                    {" "}
                    {translateLabel("txt_web_vendor_edit", "Edit")}{" "}
                  </button>

                  <button
                    onClick={() => openDeleteAddressModal(index)}
                    type="button"
                  >
                    {" "}
                    {translateLabel("lnk_delete", "Delete")}{" "}
                  </button>
                </div>
              </div>
            );
          })}

          <AccountActionButton
            className="vendor-account-info-save vendor-account-address-add"
            onClick={openAddAddress}
            width="285px"
          >
            {translateLabel("btn_add_new_address", "+ Add New Address")}
          </AccountActionButton>
        </div>
      ) : null}

      {/* =============================================== */}
      {/* MAP */}
      {/* =============================================== */}

      {isAddressMapOpen ? (
        <div className="vendor-account-map-backdrop">
          <div
            className="vendor-account-map-modal"
            role="dialog"
            aria-modal="true"
          >
            <div className="vendor-account-map-header">
              <strong>
                {translateLabel(
                  "txt_web_vendor_choose_from_map",
                  "Choose from map",
                )}
              </strong>

              <button
                aria-label={translateLabel(
                  "txt_web_vendor_close_map",
                  "Close map",
                )}
                onClick={() => setIsAddressMapOpen(false)}
                type="button"
              >
                x
              </button>
            </div>

            <p style={{ fontSize: "14px" }}>
              {" "}
              {translateLabel(
                "txt_web_vendor_click_on_the_map_to_select_your_address",
                "Click on the map to select your address.",
              )}{" "}
            </p>

            <div className="vendor-account-map-current">
              <AccountActionButton
                width="190px"
                onClick={useCurrentLocation}
                disabled={isResolvingLocation}
              >
                {isResolvingLocation
                  ? translateLabel(
                      "txt_web_vendor_getting_current_location",
                      "Getting location...",
                    )
                  : translateLabel(
                      "btn_web_vendor_use_current_location",
                      "Use my current location",
                    )}
              </AccountActionButton>
            </div>

            <div
              ref={mapContainerRef}
              style={{
                width: "100%",
                height: "420px",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            />

            {isResolvingLocation ? (
              <p>
                {translateLabel(
                  "txt_web_vendor_getting_address",
                  "Getting address...",
                )}
              </p>
            ) : null}

            {mapError ? (
              <p className="vendor-account-password-error" role="alert">
                {translateText(mapError)}
              </p>
            ) : null}

            {selectedMapLocation?.pinnedAddress ? (
              <div
                className="vendor-account-address-map-selected"
                style={{
                  marginTop: "12px",
                }}
              >
                <strong>
                  {translateLabel(
                    "txt_web_vendor_selected_address",
                    "Selected Address",
                  )}
                </strong>

                <p>{selectedMapLocation.pinnedAddress}</p>
              </div>
            ) : null}

            <div className="vendor-account-map-actions">
              <AccountActionButton
                width="150px"
                onClick={useSelectedMapLocation}
                disabled={!selectedMapLocation || isResolvingLocation}
              >
                {isResolvingLocation
                  ? translateLabel("txt_end_user_loading", "Loading...")
                  : translateLabel(
                      "txt_web_vendor_use_this_location",
                      "Use this location",
                    )}
              </AccountActionButton>

              <AccountActionButton
                color="dark"
                onClick={() => setIsAddressMapOpen(false)}
              >
                {" "}
                {translateLabel("btn_cancel", "Cancel")}{" "}
              </AccountActionButton>
            </div>
          </div>
        </div>
      ) : null}

      {/* =============================================== */}
      {/* DELETE MODAL */}
      {/* =============================================== */}

      <DeleteAddressModal
        address={selectedDeleteAddress}
        loading={isDeletingAddress}
        onClose={() => setDeletingAddressIndex(null)}
        onConfirm={deleteAddress}
        open={deletingAddressIndex !== null}
      />
    </div>
  );
}

export default AddressPanel;
